/**
 * protocol.js — the single definition of every client→server message.
 *
 * Each entry names a Socket.IO event, the shape of its payload, and its
 * rate limit. server.js registers handlers by iterating this table, so a
 * message that is not listed here cannot reach a room. The client's
 * SocketManager validates outgoing payloads against the same table in dev
 * mode, so a mismatch is caught on the sending side first.
 *
 * Field spec syntax (deliberately tiny — no dependency):
 *   'string' | 'number' | 'int' | 'boolean'         required
 *   'string?' | 'number?' | 'int?' | 'boolean?'     optional (undefined allowed)
 *   { type, min?, max?, maxLength?, enum?, optional? }  constrained
 *
 * Unknown fields are dropped from the validated value (never forwarded).
 * An ES module shared by the server (require) and the client bundle (import).
 */

  const PROTOCOL_VERSION = 1;

  const MAX_NAME_LENGTH = 20;
  const INVENTORY_SLOTS = 5;

  /** Client → server. Handler names refer to Room methods (see server.js dispatcher). */
  const ClientMessages = {
    setPlayerName: {
      fields: {
        name: { type: 'string', maxLength: 200, optional: true }, // sanitized server-side
        gameModeId: { type: 'string', maxLength: 64, optional: true },
        sessionToken: { type: 'string', maxLength: 64, optional: true },
        protocolVersion: 'int?',
        // Lobby loadout (spec 6A D6). Flat fields: the validator has no nested objects.
        skinId: { type: 'string', maxLength: 64, optional: true },
        trailId: { type: 'string', maxLength: 64, optional: true },
        backpackId: { type: 'string', maxLength: 64, optional: true }
      },
      rateLimit: { maxPerSecond: 2, burstAllowance: 2 }
    },
    ready: { fields: { gameModeId: { type: 'string', maxLength: 64, optional: true } }, rateLimit: { maxPerSecond: 2, burstAllowance: 2 } },
    unready: { fields: {}, rateLimit: { maxPerSecond: 2, burstAllowance: 2 } },
    // Debug panel "Start Game". Not devOnly: the room itself refuses it in production.
    forceStart: { fields: {}, rateLimit: { maxPerSecond: 1, burstAllowance: 1 } },

    input: {
      fields: {
        seq: { type: 'int', min: 0 },
        left: 'boolean?', right: 'boolean?', up: 'boolean?', down: 'boolean?', reload: 'boolean?', drift: 'boolean?'
      },
      rateLimit: { maxPerSecond: 65, burstAllowance: 10 }
    },
    aim: {
      fields: { seq: { type: 'int', min: 0 }, angle: 'number' },
      rateLimit: { maxPerSecond: 35, burstAllowance: 5 }
    },
    shoot: {
      fields: { angle: 'number', viewTime: 'number?', seq: { type: 'int', min: 0, max: Number.MAX_SAFE_INTEGER, optional: true } },
      rateLimit: { maxPerSecond: 15, burstAllowance: 3 }
    },
    meleeSwing: {
      fields: { angle: 'number', viewTime: 'number?', seq: { type: 'int', min: 0, max: Number.MAX_SAFE_INTEGER, optional: true } },
      rateLimit: { maxPerSecond: 4, burstAllowance: 2 }
    },
    // Generic cosmetic slot change (in-match; no client sender today — kept for a future emote/in-match equip).
    equipCosmetic: {
      fields: { slot: { type: 'string', enum: ['skin', 'trail', 'backpack'] }, id: { type: 'string', maxLength: 64 } },
      rateLimit: { maxPerSecond: 2, burstAllowance: 3 }
    },
    activateAbility: {
      fields: { category: { type: 'string', enum: ['utility', 'movement'] } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 2 }
    },
    cancelAbility: {
      fields: { abilityId: { type: 'string', maxLength: 64 } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 2 }
    },
    switchInventorySlot: {
      fields: { slotIndex: { type: 'int', min: 0, max: INVENTORY_SLOTS - 1 } },
      rateLimit: { maxPerSecond: 10, burstAllowance: 5 }
    },
    swapInventorySlots: {
      fields: {
        fromIndex: { type: 'int', min: 0, max: INVENTORY_SLOTS - 1 },
        toIndex: { type: 'int', min: 0, max: INVENTORY_SLOTS - 1 }
      },
      rateLimit: { maxPerSecond: 5, burstAllowance: 3 }
    },
    dropInventoryItem: {
      fields: { slotIndex: { type: 'int', min: 0, max: INVENTORY_SLOTS - 1 } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 3 }
    },
    dropAbility: {
      fields: { category: { type: 'string', enum: ['utility', 'movement'] } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 3 }
    },
    manualPickup: { fields: {}, rateLimit: { maxPerSecond: 5, burstAllowance: 3 } },
    // Use the active inventory slot. Which item that is, and what using it
    // does, is the server's business — the client only says "use it".
    useItem: { fields: {}, rateLimit: { maxPerSecond: 5, burstAllowance: 2 } },
    lootContainerInteract: {
      fields: { containerId: { type: 'string', maxLength: 64 } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 3 }
    },
    lootContainerCancelInteract: {
      fields: { containerId: { type: 'string', maxLength: 64 } },
      rateLimit: { maxPerSecond: 5, burstAllowance: 3 }
    },
    ping_request: {
      fields: { clientTime: 'number' },
      rateLimit: { maxPerSecond: 5, burstAllowance: 5 }
    },
    flagMoment: { fields: {}, rateLimit: { maxPerSecond: 1, burstAllowance: 3 } },
    // An eliminated player starts watching. targetId (their eliminator) is only a
    // hint: the server picks who they follow and answers with 'spectateTarget'.
    spectate: {
      fields: { targetId: { type: 'string', maxLength: 64, optional: true } },
      rateLimit: { maxPerSecond: 2, burstAllowance: 2 }
    },
    // Dev/testing only — server registers these only when testingMode.enabled
    equipAbility: {
      fields: {
        category: { type: 'string', enum: ['utility', 'movement'] },
        abilityId: { type: 'string', maxLength: 64 },
        stacks: { type: 'int', min: 1, max: 99, optional: true }
      },
      rateLimit: { maxPerSecond: 2, burstAllowance: 1 },
      devOnly: true
    },
    equipWeapon: {
      fields: { weaponId: { type: 'string', maxLength: 64 } },
      rateLimit: { maxPerSecond: 2, burstAllowance: 1 },
      devOnly: true
    }
  };

  /** Server → client event names (documentation + client-side exhaustiveness checks). */
  const ServerMessages = [
    'gameConfigs', 'spawn', 'rejoin', 'rejoinFailed', 'state', 'roomStatus', 'readyConfirmed', 'unreadyConfirmed',
    'gameStarting', 'gameEnd', 'error', 'pong_response', 'protocolMismatch', 'cosmeticsUnlocked',
    'rankedResult', 'joinRejected', 'leveledUp', 'flagAck', 'netProbe', 'hitregDebug', 'spectateTarget'
  ];

  // ───────────────────────── validator ─────────────────────────

  function normalizeSpec(spec) {
    if (typeof spec === 'string') {
      const optional = spec.endsWith('?');
      return { type: optional ? spec.slice(0, -1) : spec, optional };
    }
    return { optional: false, ...spec };
  }

  function checkField(name, value, spec) {
    if (value === undefined || value === null) {
      return spec.optional ? { ok: true, value: undefined } : { ok: false, error: `${name} is required` };
    }
    switch (spec.type) {
      case 'string':
        if (typeof value !== 'string') return { ok: false, error: `${name} must be a string` };
        if (spec.maxLength !== undefined && value.length > spec.maxLength) return { ok: false, error: `${name} exceeds ${spec.maxLength} chars` };
        if (spec.enum && !spec.enum.includes(value)) return { ok: false, error: `${name} must be one of ${spec.enum.join('|')}` };
        return { ok: true, value };
      case 'number':
      case 'int':
        if (typeof value !== 'number' || !Number.isFinite(value)) return { ok: false, error: `${name} must be a finite number` };
        if (spec.type === 'int' && !Number.isInteger(value)) return { ok: false, error: `${name} must be an integer` };
        if (spec.min !== undefined && value < spec.min) return { ok: false, error: `${name} below ${spec.min}` };
        if (spec.max !== undefined && value > spec.max) return { ok: false, error: `${name} above ${spec.max}` };
        return { ok: true, value };
      case 'boolean':
        if (typeof value !== 'boolean') return { ok: false, error: `${name} must be a boolean` };
        return { ok: true, value };
      default:
        return { ok: false, error: `${name}: unknown spec type ${spec.type}` };
    }
  }

  /**
   * Validate a client message payload against its schema.
   * @returns {{ ok: boolean, value?: Object, error?: string }}
   */
  function validateClientMessage(eventName, payload) {
    const def = ClientMessages[eventName];
    if (!def) return { ok: false, error: `unknown message ${eventName}` };
    const raw = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const value = {};
    for (const [field, rawSpec] of Object.entries(def.fields)) {
      const spec = normalizeSpec(rawSpec);
      const res = checkField(field, raw[field], spec);
      if (!res.ok) return res;
      if (res.value !== undefined) value[field] = res.value;
    }
    return { ok: true, value };
  }

  const Protocol = {
    PROTOCOL_VERSION,
    MAX_NAME_LENGTH,
    INVENTORY_SLOTS,
    ClientMessages,
    ServerMessages,
    validateClientMessage
  };

export { PROTOCOL_VERSION, MAX_NAME_LENGTH, INVENTORY_SLOTS, ClientMessages, ServerMessages, validateClientMessage, Protocol };
